/**
 * 输入框组件
 */
import { adjustTextareaHeight } from '../utils/ui.js';
import { MAX_FILE_SIZE, ALLOWED_IMAGE_TYPES } from '../config.js';

export type UploadState = 'idle' | 'uploading';
export type SendKeyMode = 'enter' | 'alt-enter';

export interface PendingImage {
  url: string;
  name: string;
}

export interface InputBoxOptions {
  onSendMessage: (content: string) => void;
  onTypingChange: (isTyping: boolean) => void;
  onFileUpload: (file: File) => Promise<string>; // 返回图片 URL
}

export class InputBox {
  private options: InputBoxOptions;
  private form: HTMLFormElement | null = null;
  private input: HTMLTextAreaElement | null = null;
  private fileInput: HTMLInputElement | null = null;
  private uploadBtn: HTMLElement | null = null;
  private typingTimeout: ReturnType<typeof setTimeout> | null = null;
  private uploadState: UploadState = 'idle';
  private sendKeyMode: SendKeyMode = 'alt-enter'; // 默认 Alt+Enter 发送
  private pendingImages: PendingImage[] = []; // 待发送的图片列表
  private previewContainer: HTMLElement | null = null; // 图片预览容器

  constructor(options: InputBoxOptions) {
    this.options = options;
    // 从 localStorage 读取设置
    this.loadSettings();
  }

  /**
   * 加载用户设置
   */
  private loadSettings(): void {
    try {
      const saved = localStorage.getItem('chat-send-key-mode');
      if (saved === 'enter' || saved === 'alt-enter') {
        this.sendKeyMode = saved;
      }
    } catch {
      // 使用默认值
    }
  }

  /**
   * 获取当前发送快捷键模式
   */
  getSendKeyMode(): SendKeyMode {
    return this.sendKeyMode;
  }

  /**
   * 设置发送快捷键模式
   */
  setSendKeyMode(mode: SendKeyMode): void {
    this.sendKeyMode = mode;
    try {
      localStorage.setItem('chat-send-key-mode', mode);
    } catch {
      // 保存失败，忽略
    }
    this.updatePlaceholder();
  }

  /**
   * 更新输入框提示文字
   */
  private updatePlaceholder(): void {
    if (!this.input) return;
    if (this.sendKeyMode === 'enter') {
      this.input.placeholder = '输入消息... (Shift+Enter 换行)';
    } else {
      this.input.placeholder = '输入消息... (Alt+Enter 发送)';
    }
  }

  /**
   * 初始化组件
   */
  private init(): void {
    this.form = document.getElementById('chat-form') as HTMLFormElement;
    this.input = document.getElementById('message-input') as HTMLTextAreaElement;
    this.fileInput = document.getElementById('file-input') as HTMLInputElement;
    this.uploadBtn = document.getElementById('upload-btn');

    if (!this.form || !this.input) {
      console.error('Input box elements not found');
      return;
    }

    // 设置初始 placeholder
    this.updatePlaceholder();

    // 绑定表单提交事件
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));

    // 绑定输入事件
    this.input.addEventListener('input', () => this.handleInput());

    // 绑定键盘事件
    this.input.addEventListener('keydown', (e) => this.handleKeydown(e));

    // 绑定文件上传按钮
    this.uploadBtn?.addEventListener('click', () => {
      this.fileInput?.click();
    });

    // 绑定文件选择事件
    this.fileInput?.addEventListener('change', (e) => this.handleFileSelect(e));
  }

  /**
   * 处理表单提交
   */
  private handleSubmit(e: Event): void {
    e.preventDefault();

    if (!this.input) return;

    const content = this.input.value.trim();
    if (this.uploadState === 'uploading') return;

    // 如果没有文字也没有图片，不发送
    if (!content && this.pendingImages.length === 0) return;

    // 构建最终内容：图片 + 文字
    let finalContent = '';

    // 添加图片 markdown
    this.pendingImages.forEach(img => {
      finalContent += `![${img.name}](${img.url})\n`;
    });

    // 添加文字内容
    finalContent += content;

    // 发送消息
    this.options.onSendMessage(finalContent);

    // 清空输入框和待发送图片
    this.input.value = '';
    this.pendingImages = [];
    this.renderPreview();
    adjustTextareaHeight(this.input);

    // 通知打字状态变化
    this.notifyTypingChange(false);
  }

  /**
   * 处理输入事件
   */
  private handleInput(): void {
    if (!this.input) return;

    // 调整高度
    adjustTextareaHeight(this.input);

    // 通知打字状态变化（防抖）
    this.notifyTypingChange(this.input.value.length > 0);
  }

  /**
   * 处理键盘事件
   */
  private handleKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter') {
      if (this.sendKeyMode === 'enter') {
        // Enter 模式：Enter 发送，Shift+Enter 换行
        if (!e.shiftKey) {
          e.preventDefault();
          this.form?.dispatchEvent(new Event('submit'));
        }
        // Shift+Enter 保持默认换行行为
      } else {
        // Alt+Enter 模式：Alt+Enter 发送，Enter 换行
        if (e.altKey) {
          e.preventDefault();
          this.form?.dispatchEvent(new Event('submit'));
        }
        // Enter 保持默认换行行为
      }
    }
  }

  /**
   * 处理文件选择
   */
  private async handleFileSelect(e: Event): Promise<void> {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];

    if (!file) return;

    // 验证文件类型
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      alert('只能上传图片文件');
      return;
    }

    // 验证文件大小
    if (file.size > MAX_FILE_SIZE) {
      alert('文件大小不能超过 10MB');
      return;
    }

    try {
      // 设置上传状态
      this.setUploadState('uploading');

      // 处理上传，获取图片 URL
      const imageUrl = await this.options.onFileUpload(file);

      // 添加到待发送列表
      this.pendingImages.push({ url: imageUrl, name: file.name });

      // 更新预览
      this.renderPreview();
    } catch (error) {
      console.error('Upload error:', error);
      const message = error instanceof Error ? error.message : '上传失败，请重试';
      alert(message);
    } finally {
      // 恢复上传状态
      this.setUploadState('idle');
    }

    // 清空文件输入
    if (this.fileInput) {
      this.fileInput.value = '';
    }
  }

  /**
   * 通知打字状态变化（防抖）
   */
  private notifyTypingChange(isTyping: boolean): void {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }

    // 立即通知开始打字
    if (isTyping) {
      this.options.onTypingChange(true);
    } else {
      // 停止打字延迟通知
      this.typingTimeout = setTimeout(() => {
        this.options.onTypingChange(false);
      }, 1000);
    }
  }

  /**
   * 渲染图片预览
   */
  private renderPreview(): void {
    // 查找或创建预览容器
    let container = document.getElementById('image-preview-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'image-preview-container';
      container.className = 'image-preview-container';
      // 插入到输入框之前
      this.input?.parentNode?.insertBefore(container, this.input);
    }
    this.previewContainer = container;

    if (this.pendingImages.length === 0) {
      container.style.display = 'none';
      return;
    }

    container.style.display = 'flex';
    container.innerHTML = '';

    this.pendingImages.forEach((img, index) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'image-preview-item';

      const imgEl = document.createElement('img');
      imgEl.src = img.url;
      imgEl.alt = img.name;

      const removeBtn = document.createElement('button');
      removeBtn.className = 'image-preview-remove';
      removeBtn.innerHTML = '×';
      removeBtn.onclick = () => this.removeImage(index);

      wrapper.appendChild(imgEl);
      wrapper.appendChild(removeBtn);
      container.appendChild(wrapper);
    });
  }

  /**
   * 移除待发送的图片
   */
  private removeImage(index: number): void {
    this.pendingImages.splice(index, 1);
    this.renderPreview();
  }

  /**
   * 设置上传状态
   */
  setUploadState(state: UploadState): void {
    this.uploadState = state;

    if (!this.uploadBtn) return;

    switch (state) {
      case 'idle':
        this.uploadBtn.textContent = '📎';
        this.uploadBtn.disabled = false;
        break;
      case 'uploading':
        this.uploadBtn.textContent = '⏳';
        this.uploadBtn.disabled = true;
        break;
    }
  }

  /**
   * 聚焦输入框
   */
  focus(): void {
    this.input?.focus();
  }

  /**
   * 显示组件
   */
  show(): void {
    this.init();
    this.focus();
  }

  /**
   * 销毁组件
   */
  destroy(): void {
    if (this.form) {
      this.form.removeEventListener('submit', (e) => this.handleSubmit(e));
    }

    if (this.input) {
      this.input.removeEventListener('input', () => this.handleInput());
      this.input.removeEventListener('keydown', (e) => this.handleKeydown());
    }

    if (this.uploadBtn) {
      this.uploadBtn.removeEventListener('click', () => {
        this.fileInput?.click();
      });
    }

    if (this.fileInput) {
      this.fileInput.removeEventListener('change', (e) => this.handleFileSelect(e));
    }

    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }
  }
}
